import { useLocation, useNavigate } from "react-router-dom";
import { ArrowUpRight, Zap } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { CONTACT, mailtoConsult } from "@/lib/contact";
import { nav } from "@/lib/home-content";
import { companyPaths } from "@/lib/offers";

export default function Footer() {
    const navigate = useNavigate();
    const location = useLocation();
    const isCompany = companyPaths.includes(location.pathname);

    const goToSection = (href) => {
        if (href.startsWith("/")) {
            const hashIndex = href.indexOf("#");
            if (hashIndex !== -1 && location.pathname === "/") {
                scrollToId(href.slice(hashIndex));
                return;
            }
            navigate(href);
            return;
        }
        if (location.pathname === "/") {
            scrollToId(href);
        } else {
            navigate(`/${href}`);
        }
    };

    if (isCompany) {
        return (
            <footer data-testid="footer" className="bg-[#0a0a0f] px-5 py-12 text-white">
                <div className="mx-auto max-w-5xl">
                    <div className="flex flex-col items-start justify-between gap-8 sm:flex-row">
                        <div className="max-w-xs">
                            <div className="flex items-center gap-2">
                                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--brand)] text-white">
                                    <Zap className="h-4 w-4" fill="currentColor" strokeWidth={1.5} />
                                </span>
                                <span className="font-display text-lg font-extrabold">SalesCan</span>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-white/60">
                                William Narrie&apos;s company. Founder-led from St. Catharines since 2022. Revenue
                                systems, sites, and products — you own the work.
                            </p>
                        </div>

                        <div className="flex flex-wrap gap-x-8 gap-y-2">
                            {nav.map((n) => (
                                <button
                                    key={n.href}
                                    type="button"
                                    data-testid={`footer-link-${n.label.toLowerCase()}`}
                                    onClick={() => goToSection(n.href)}
                                    className="text-sm font-medium text-white/70 transition-colors hover:text-white"
                                >
                                    {n.label}
                                </button>
                            ))}
                        </div>

                        <div className="text-sm text-white/60">
                            <a href={mailtoConsult} className="block hover:text-white">
                                {CONTACT.email}
                            </a>
                            <a href={`tel:${CONTACT.phoneTel}`} className="mt-1 block hover:text-white">
                                {CONTACT.phoneDisplay}
                            </a>
                            <p className="mt-2">{CONTACT.address}</p>
                        </div>
                    </div>

                    <div className="mt-10 border-t border-white/10 pt-6 text-xs text-white/40">
                        © {new Date().getFullYear()} SalesCan. Founder-led, St. Catharines, Canada.
                    </div>
                </div>
            </footer>
        );
    }

    return (
        <footer data-testid="footer" className="border-t border-white/10 px-4 sm:px-8 lg:px-16 py-12 bg-[#0a0a0f]">
            <div className="max-w-[1400px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                <div>
                    <img src="/salescan-logo.png" alt="SalesCan" className="h-8 w-auto mb-3" />
                    <p className="text-sm text-slate-500 max-w-xs">
                        Sales performance engineering for ambitious businesses. Analytics, automations and AI — built in
                        Canada.
                    </p>
                    <p className="text-sm text-slate-500 mt-3">{CONTACT.address}</p>
                    <a href={mailtoConsult} className="block text-sm text-slate-400 hover:text-white mt-2">
                        {CONTACT.email}
                    </a>
                    <a href={`tel:${CONTACT.phoneTel}`} className="block text-sm text-slate-400 hover:text-white">
                        {CONTACT.phoneDisplay}
                    </a>
                </div>
                <button
                    data-testid="footer-cta-button"
                    onClick={() => navigate("/")}
                    className="group flex items-center gap-2 text-xs font-semibold text-[var(--brand)] hover:text-white uppercase tracking-[0.2em] transition-colors duration-300"
                >
                    Home
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
            </div>
            <div className="max-w-[1400px] mx-auto mt-10 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-3">
                <p className="text-xs text-slate-600">© {new Date().getFullYear()} SalesCan. All rights reserved.</p>
                <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-slate-600">
                    Sell faster. Close smarter.
                </p>
            </div>
        </footer>
    );
}
